import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ensureOk, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

type S = components['schemas'];
export type FechaClave = S['EnarmMilestone'];
export type EtapaEnarm = S['EnarmEvent'];
export type EtapaBody = S['EnarmEventCreateBody'];

/** Etapas típicas del calendario oficial (CIFRHS), para no escribir el nombre cada ciclo. */
export const ETAPAS_TIPICAS = [
  'Preregistro en línea',
  'Pago del examen',
  'Registro electrónico',
  'Revisión documental',
  'Cierre de aclaraciones',
  'Publicación de sedes',
  'Aplicación del examen',
  'Publicación de resultados',
  'Guía de selección de plazas',
  'Inicio de residencias',
] as const;

/** Fechas clave (`cat__enarm`): la plataforma las lee por id. */
export function useFechasClave() {
  return useQuery({
    queryKey: ['configuracion', 'enarm', 'fechas'],
    queryFn: () => unwrap<FechaClave[]>(api.GET('/api/v1/admin/enarm/milestones')),
  });
}

export function useGuardarFechaClave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name: string; date: string | null }) =>
      unwrap<FechaClave>(
        api.PATCH('/api/v1/admin/enarm/milestones/{id}', {
          params: { path: { id: String(id) } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['configuracion', 'enarm', 'fechas'] }),
  });
}

/** Etapas del calendario ENARM que ven los alumnos en "Mi plan de estudio". */
export function useEtapas() {
  return useQuery({
    queryKey: ['configuracion', 'enarm', 'etapas'],
    queryFn: () => unwrap<EtapaEnarm[]>(api.GET('/api/v1/admin/enarm/events')),
  });
}

export function useGuardarEtapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id?: number;
      body: Partial<EtapaBody> & { title: string; date: string };
    }) =>
      id === undefined
        ? unwrap<EtapaEnarm>(api.POST('/api/v1/admin/enarm/events', { body }))
        : unwrap<EtapaEnarm>(
            api.PATCH('/api/v1/admin/enarm/events/{id}', {
              params: { path: { id: String(id) } },
              body,
            }),
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['configuracion', 'enarm', 'etapas'] }),
  });
}

export function useBorrarEtapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      ensureOk(
        api.DELETE('/api/v1/admin/enarm/events/{id}', { params: { path: { id: String(id) } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['configuracion', 'enarm', 'etapas'] }),
  });
}
